import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { roleHome } from '../utils/roleHome';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  // Ziel aus ProtectedRoute (z. B. /task/123 aus einer Mail); Fremd-URLs ignorieren.
  const from = typeof location.state?.from === 'string' && location.state.from.startsWith('/')
    && !location.state.from.startsWith('//') && !location.state.from.includes('\\')
    && !location.state.from.startsWith('/login')
    ? location.state.from
    : null;
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already logged in: zurück zum ursprünglichen Ziel, sonst
  // role-aware (clients -> PM home)
  useEffect(() => {
    if (user) {
      navigate(from || roleHome(user), { replace: true });
    }
  }, [user, from, navigate]);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(credentials.username, credentials.password);

      if (result.success) {
        // Navigation happens automatically via the role-aware useEffect above
      } else {
        setError(result.error || 'Login fehlgeschlagen');
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Login fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2d4a6f 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: 4,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(2px 2px at 20% 30%, white, transparent),
                           radial-gradient(2px 2px at 60% 70%, white, transparent),
                           radial-gradient(1px 1px at 50% 50%, white, transparent),
                           radial-gradient(1px 1px at 80% 10%, white, transparent),
                           radial-gradient(2px 2px at 90% 60%, white, transparent),
                           radial-gradient(1px 1px at 33% 80%, white, transparent),
                           radial-gradient(1px 1px at 15% 90%, white, transparent)`,
          backgroundSize: '100% 100%',
          opacity: 0.6,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={12}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {/* RedOrbit Original Logo - Inverted for light background */}
            <Box
              sx={{
                display: 'inline-block',
                mb: 3,
                mt: 2,
              }}
            >
              <img
                src="/redorbit_logo.svg"
                alt="RedOrbit"
                style={{
                  height: '40px',
                  width: 'auto',
                  filter: 'invert(1) brightness(0)',
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Time Tracker - Melden Sie sich an
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Benutzername"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
              autoComplete="username"
              disabled={loading}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Passwort"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              disabled={loading}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                boxShadow: '0 4px 12px rgba(224, 30, 38, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ff4449 0%, #ff5a5f 100%)',
                  boxShadow: '0 6px 20px rgba(224, 30, 38, 0.4)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: '#ccc',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 RedOrbit Time Tracker
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AI Superheroes at your Service
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
